import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BrandLogo } from "@/components/BrandLogo";
import { Truck, PackageCheck, BadgeCheck, AlertCircle, MailWarning } from "lucide-react";
import { toast } from "sonner";

export default function Auth() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setUser } = useAuth();

  const [mode, setMode] = useState(params.get("mode") === "signup" ? "signup" : "login");
  const [role, setRole] = useState(params.get("role") === "removalist" ? "removalist" : "customer");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company_name: "", abn_or_license: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const nextParam = params.get("next");
  const destFor = (u) => {
    const safe = nextParam && /^\/[A-Za-z0-9\-_/?=&]*$/.test(nextParam) && !nextParam.startsWith("//");
    if (safe && (u?.role === "customer" || (u?.role === "removalist" && nextParam.startsWith("/claim/")))) return nextParam;
    return "/dashboard";
  };

  useEffect(() => {
    if (user && user !== false) navigate(destFor(user), { replace: true });
  }, [user, navigate]); // eslint-disable-line react-hooks/exhaustive-deps

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      let data;
      if (mode === "signup") {
        ({ data } = await api.post("/auth/register", { ...form, role }));
        setUser(data);
        toast.success("Welcome to MoveTender!");
      } else {
        ({ data } = await api.post("/auth/login", { email: form.email, password: form.password }));
        setUser(data);
        toast.success(`Welcome back, ${data.name.split(" ")[0]}!`);
      }
      navigate(destFor(data), { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    localStorage.setItem("pending_role", role);
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      <div className="hidden lg:block relative bg-asphalt overflow-hidden">
        <img src="https://images.unsplash.com/photo-1602750766769-8db8d49cc369?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
          alt="Moving van" className="absolute inset-0 h-full w-full object-cover opacity-40" />
        <div className="grain-overlay absolute inset-0" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <BrandLogo light />
          </div>
          <div>
            <h2 className="font-display text-4xl font-extrabold leading-tight">The auction floor<br />for every move.</h2>
            <p className="mt-4 text-white/70 max-w-md">Join thousands of job owners and removalists trading moves at the best possible price.</p>
          </div>
          <p className="font-mono text-xs text-white/40 uppercase tracking-widest">Removalist Job Marketplace</p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <BrandLogo />
          </div>

          <Tabs value={mode} onValueChange={setMode} className="w-full">
            <TabsList className="grid grid-cols-2 w-full mb-6">
              <TabsTrigger value="login" data-testid="tab-login">Log in</TabsTrigger>
              <TabsTrigger value="signup" data-testid="tab-signup">Sign up</TabsTrigger>
            </TabsList>

            <h1 className="font-display text-2xl font-bold mb-1">
              {mode === "signup" ? "Create your account" : "Welcome back"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {mode === "signup" ? "Pick your role to get started." : "Log in to your MoveTender dashboard."}
            </p>

            {mode === "signup" && (
              <div className="grid grid-cols-2 gap-3 mb-6">
                <button type="button" onClick={() => setRole("customer")} data-testid="role-select-customer"
                  className={`rounded-xl border-2 p-4 text-left transition-all ${role === "customer" ? "border-kinetic bg-kinetic-light" : "border-border hover:border-slate-300"}`}>
                  <PackageCheck className={`h-6 w-6 mb-2 ${role === "customer" ? "text-kinetic" : "text-muted-foreground"}`} />
                  <p className="font-semibold text-sm">Customer</p>
                  <p className="text-xs text-muted-foreground">Post jobs & get bids</p>
                </button>
                <button type="button" onClick={() => setRole("removalist")} data-testid="role-select-removalist"
                  className={`rounded-xl border-2 p-4 text-left transition-all ${role === "removalist" ? "border-kinetic bg-kinetic-light" : "border-border hover:border-slate-300"}`}>
                  <Truck className={`h-6 w-6 mb-2 ${role === "removalist" ? "text-kinetic" : "text-muted-foreground"}`} />
                  <p className="font-semibold text-sm">Removalist</p>
                  <p className="text-xs text-muted-foreground">Bid & win jobs</p>
                </button>
              </div>
            )}

            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-4" data-testid="auth-error">
                <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" /> <span>{error}</span>
              </div>
            )}

            <form onSubmit={submit} className="space-y-4">
              {mode === "signup" && (
                <div className="space-y-2">
                  <Label htmlFor="name">Full name</Label>
                  <Input id="name" data-testid="signup-name-input" value={form.name} onChange={upd("name")} required placeholder="Jane Doe" />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input id="email" type="email" data-testid="email-input" value={form.email} onChange={upd("email")} required placeholder="you@example.com" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input id="password" type="password" data-testid="password-input" value={form.password} onChange={upd("password")} required placeholder="••••••••" />
              </div>

              {mode === "signup" && (
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone</Label>
                  <Input id="phone" data-testid="signup-phone-input" value={form.phone} onChange={upd("phone")} placeholder="+61 400 000 000" />
                </div>
              )}

              {mode === "signup" && role === "removalist" && (
                <Card className="p-4 bg-secondary/50 space-y-4">
                  <div className="flex items-center gap-2 text-sm font-medium text-emerald-700">
                    <BadgeCheck className="h-4 w-4" /> Auto-verified on signup
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="company">Company name</Label>
                    <Input id="company" data-testid="signup-company-input" value={form.company_name} onChange={upd("company_name")} placeholder="Carter Removals Pty Ltd" />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="abn">ABN / License</Label>
                    <Input id="abn" data-testid="signup-abn-input" value={form.abn_or_license} onChange={upd("abn_or_license")} placeholder="ABN 12 345 678 901" />
                  </div>
                </Card>
              )}

              <Button type="submit" disabled={loading}
                data-testid={mode === "signup" ? "signup-submit-btn" : "login-submit-btn"}
                className="w-full h-11 bg-kinetic hover:bg-kinetic-hover text-white">
                {loading ? "Please wait…" : mode === "signup" ? "Create account" : "Log in"}
              </Button>
              {mode === "signup" && (
                <p className="flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 text-amber-800 text-xs p-3" data-testid="signup-spam-note">
                  <MailWarning className="h-4 w-4 shrink-0 mt-px" />
                  <span>Check your Spam folder for our emails and mark them Not spam, so you don't miss quotes and updates.</span>
                </p>
              )}
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="h-px flex-1 bg-border" />
              <span className="text-xs text-muted-foreground uppercase font-mono">or</span>
              <div className="h-px flex-1 bg-border" />
            </div>

            <Button type="button" variant="outline" onClick={googleLogin} data-testid="google-login-btn" className="w-full h-11 gap-2 border-2">
              <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
              Continue with Google
            </Button>
            <p className="text-xs text-center text-muted-foreground mt-3">
              {mode === "signup" && `You'll sign up as a ${role}.`}
            </p>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
